// ResultsModal.js - Fixed version to show questions with only _info data

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Modal,
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './PreScreeningModal.css';
import { message } from 'antd';

function ResultsModal({ open, setOpen, record, backendURL, getCookie }) {
    const questionMap = {
        Q1: "Candidate's current salary including superannuation in AUD.",
        Q2: "Candidate's salary expectations for the job including superannuation in AUD.",
        Q3: "Candidate's has the right to work in Australia.",
        Q4: "Candidate's notice period (in days).",
        Q5: "Candidate's first availability for an interview.",
        Q6: "Candidate's alternate availability for an interview.",
        Q7: "Is the candidate currently interviewing with other companies.",
        Q8: "If the candidate will accept fully remote work arrangements.",
        Q9: "If the candidate will accept hybrid work arrangements.",
        Q10: "If the candidate will accept fully in-person work arrangements.",
        Q11: "What is your location, city & suburb?",
        Q12: "Do you have your own transport?",
        Q13: "What is your hourly rate?",
        Q14: "What is your daily rate?",
        Q15: "What day are you available to start?",
        Q16: "What is your security clearance level?",
    };

    const [candidateData, setCandidateData] = useState(null);
    const [qaPairs, setQaPairs] = useState(null);
    const [shouldOpen, setShouldOpen] = useState(false);
    const [hasInfo, setHasInfo] = useState(false);

    useEffect(() => {
        if (open) {
            setShouldOpen(false);
            getResults();
        };
    }, [open]);

    const getResults = () => {
        console.log("Attempting to call backend for prescreening results")
        const queryURL = "?sessionID=" + getCookie("userSession") + "&applicationID=" + record.ApplicationID;
        axios.get(backendURL + '/v1/prescreen-result' + queryURL)
            .then(response => {
                if (response.status === 204 || !response.data || Object.keys(response.data).length === 0) {
                    throw new Error('No prescreening results available');
                }
                console.log('Prescreening results retrieved:', response);
                setCandidateData(response.data);
                setShouldOpen(true);

                const allQuestionNumbers = new Set();

                Object.keys(response.data).forEach(key => {
                    if (key.startsWith('Q')) {
                        if (key.includes('_info')) {
                            // Extract question number from info key (e.g., "Q15_info" -> "Q15")
                            const questionNumber = key.split('_')[0];
                            allQuestionNumbers.add(questionNumber);
                        } else {
                            // Main question key (e.g., "Q11")
                            allQuestionNumbers.add(key);
                        }
                    }
                });

                // Now process each unique question number
                const qaPairs = Array.from(allQuestionNumbers)
                    .filter(questionKey => {
                        // Include question if either:
                        // 1. Main answer has content, OR
                        // 2. Corresponding info field has content
                        const mainAnswer = response.data[questionKey];
                        const infoAnswer = response.data[questionKey + '_info'];

                        const hasMainContent = mainAnswer !== '' && mainAnswer !== null && mainAnswer !== undefined;
                        const hasInfoContent = infoAnswer && infoAnswer.trim() !== '';

                        return hasMainContent || hasInfoContent;
                    })
                    .map((questionKey) => {
                        let answer = response.data[questionKey];
                        console.log("Key: ", questionKey);
                        console.log("Answer: ", answer);

                        // Handle null/undefined answers
                        if (answer === null || answer === undefined) {
                            answer = 'Not specified'; // Show placeholder instead of hiding the question
                        } else if (typeof answer === 'number') {
                            answer = answer.toString();
                        } else {
                            answer = String(answer);
                        }

                        return {
                            key: questionKey,
                            question: questionMap[questionKey],
                            answer: answer === 'Not specified' ? answer : answer.charAt(0).toUpperCase() + answer.slice(1),
                            info: response.data[questionKey + '_info'] || '',
                        };
                    })
                    .sort((a, b) => parseInt(a.key.slice(1)) - parseInt(b.key.slice(1)));

                setQaPairs(qaPairs);
                setHasInfo(Object.keys(response.data).some(key => key.endsWith('_info')));

            })
            .catch(error => {
                console.error('Error retrieving prescreening results:', error);
                setShouldOpen(false);
                message.warning('No prescreening results are available for this applicant.');
                closeModal();
            });
    };

    // Modal style for scrolling and layout
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        borderRadius: '8px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        p: '30px',
        overflowY: 'auto',
    };

    const modalStyleWide = {
        ...modalStyle,
        maxWidth: '900px',
    };

    const closeModal = () => {
        setOpen(false);
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return isNaN(date)
            ? dateString
            : date.toLocaleString('en-AU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
    };

    const formatAnswer = (key, answer) => {
        console.log("Key: ", key);
        console.log("Answer in format answer: ", answer);

        // Handle "Not specified" case
        if (answer === 'Not specified') {
            return answer;
        }

        // Yes/No questions
        if (key === 'Q3' || key === 'Q7' || key === 'Q8' || key === 'Q9' || key === 'Q10' || key === 'Q12') {
            if (answer === true || String(answer).toLowerCase() === "true") {
                return 'Yes';
            } else if (answer === false || String(answer).toLowerCase() === "false") {
                return 'No';
            } else {
                return String(answer);
            }
        }
        // Date/time questions
        else if (key === 'Q5' || key === 'Q6') {
            return formatDate(answer);
        }
        // Date only question (Q15 - start date)
        else if (key === 'Q15') {
            const date = new Date(answer);
            return isNaN(date)
                ? answer
                : date.toLocaleDateString('en-AU', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                });
        }
        // Annual salary questions
        else if (key === 'Q1' || key === 'Q2') {
            return `${answer} AUD annually`;
        }
        // Hourly rate
        else if (key === 'Q13') {
            return `${answer} AUD per hour`;
        }
        // Daily rate
        else if (key === 'Q14') {
            return `${answer} AUD per day`;
        }
        // Notice period (Q4)
        else if (key === 'Q4') {
            return `${answer} days`;
        }
        // All other questions (Q11, Q16, etc.)
        else {
            return answer;
        }
    };

    return (
        <>
        {shouldOpen && (
            <Modal
                open={open}
                onClose={closeModal}
                destroyOnClose={true}
                aria-labelledby="modal-title"
                BackdropProps={{
                    style: {
                        backdropFilter: 'blur(3px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    },
                }}
            >
                <Box sx={hasInfo ? modalStyleWide : modalStyle}>
                    <Box display="flex" alignItems="center" mb={2}>
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 40, mr: 1 }} />
                        <Typography
                            id="modal-title"
                            variant="h5"
                            component="h2"
                            sx={{ color: '#303f9f', fontWeight: 600 }}
                        >
                            Pre-Screening Results
                        </Typography>
                    </Box>

                    <Typography variant="subtitle1" sx={{ color: '#666', mb: 3 }}>
                        Candidate Name:{' '}
                        <span style={{ fontWeight: 500, color: '#333' }}>
                            {record ? record.Name : ''}
                        </span>
                    </Typography>

                    <TableContainer component={Paper} sx={{ boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)', mb: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600, width: hasInfo ? '40%' : '60%' }}>Question</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: hasInfo ? '30%' : '40%' }}>Answer</TableCell>
                                    {hasInfo && <TableCell sx={{ fontWeight: 600, width: '30%' }}>Notes</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {qaPairs.map((qa, index) => (
                                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                        <TableCell sx={{ fontWeight: 500 }}>{qa.question}</TableCell>
                                        <TableCell>{formatAnswer(qa.key, qa.answer)}</TableCell>
                                        {hasInfo && <TableCell>{qa.info}</TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            onClick={closeModal}
                            sx={{
                                backgroundColor: '#d32f2f',
                                color: '#fff',
                                textTransform: 'none',
                                padding: '10px 20px',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: '#b71c1c',
                                },
                            }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>
        )}
    </>
    );
}

export default ResultsModal;